import React, { useState, useEffect } from 'react';
import { Card, Form, Row, Col, Button, Alert, Spinner, Table } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faCalendarCheck, faBuilding, faUser, faPhone, 
  faMapMarkerAlt, faInfoCircle, faTimes, faSave, 
  faTrash, faPlus
} from '@fortawesome/free-solid-svg-icons';
import { toast } from 'react-toastify';
import { useNavigate, useParams } from 'react-router-dom';
import api from '../../utils/api';
import axios from "axios";

// Perbaiki endpoint dan field mapping untuk kompatibilitas dengan backend
const FormRealisasiMultiKunjungan = () => {
  const { visitId } = useParams();
  const navigate = useNavigate();
  
  // State untuk data kunjungan yang direncanakan (plan)
  const [visitPlan, setVisitPlan] = useState(null);
  
  // State untuk multiple realisasi kunjungan
  const [kunjunganList, setKunjunganList] = useState([{ 
    id: Date.now(),
    toko: '',
    alamat: '',
    kontak_person: '',
    jabatan: '',
    telp: '',
    email: '',
    hasil_kunjungan: '',
    catatan: '',
    tindak_lanjut: '',
    potensi_order: ''
  }]);
  
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  
  // Load visit plan data
  useEffect(() => {
    const fetchVisitPlan = async () => {
      setLoading(true);
      setError(null);
      
      try {
        if (visitId) {
          try {
            // Gunakan endpoint yang benar
            const response = await api.get(`/rr/visits/${visitId}/`);
            if (response.data) {
              setVisitPlan(response.data);
              console.log('Visit plan loaded:', response.data);
            }
          } catch (apiError) {
            console.warn("API error, checking localStorage:", apiError);
            
            // Fallback ke localStorage
            const offlineVisits = JSON.parse(localStorage.getItem('offlineRRVisits') || '[]');
            const localPlan = offlineVisits.find(v => v.id.toString() === visitId.toString());
            
            if (localPlan) {
              setVisitPlan(localPlan);
            } else {
              setError("Tidak dapat menemukan data kunjungan yang direncanakan");
            }
          }
        } else {
          // Jika tidak ada visitId, ambil dari localStorage currentVisitPlan
          const localPlan = localStorage.getItem('currentVisitPlan');
          if (localPlan) {
            setVisitPlan(JSON.parse(localPlan));
          }
        }
      } catch (error) {
        console.error("Error fetching visit plan:", error);
        setError("Gagal memuat data kunjungan yang direncanakan");
      } finally {
        setLoading(false);
      }
    };
    
    fetchVisitPlan();
  }, [visitId]);
  
  // Tambah form kunjungan baru
  const addKunjungan = () => {
    setKunjunganList([...kunjunganList, { 
      id: Date.now(),
      toko: '',
      alamat: '',
      kontak_person: '',
      jabatan: '',
      telp: '',
      email: '',
      hasil_kunjungan: '',
      catatan: '',
      tindak_lanjut: '',
      potensi_order: ''
    }]);
  };
  
  // Hapus form kunjungan
  const removeKunjungan = (id) => {
    if (kunjunganList.length > 1) {
      setKunjunganList(kunjunganList.filter(k => k.id !== id));
    } else {
      toast.warning("Minimal harus ada satu realisasi kunjungan");
    }
  };
  
  // Handle perubahan data per kunjungan
  const handleKunjunganChange = (id, field, value) => {
    setKunjunganList(kunjunganList.map(k => 
      k.id === id ? { ...k, [field]: value } : k
    ));
  };
  
  // Handle submit
  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    
    try {
      // Validasi minimal data required
      const validKunjungan = kunjunganList.filter(k => 
        k.toko?.trim() && k.alamat?.trim() && k.kontak_person?.trim() && 
        k.telp?.trim() && k.hasil_kunjungan
      );
      
      if (validKunjungan.length === 0) {
        toast.error("Minimal satu kunjungan harus diisi dengan lengkap");
        setSubmitting(false);
        return;
      }
      
      console.log(`Submitting ${validKunjungan.length} valid visits`);
      
      // Prepare data untuk setiap realisasi
      const realisasiKunjunganData = validKunjungan.map(kunjungan => ({
        plan: visitPlan?.id || null,
        tanggal: new Date().toISOString().split('T')[0],
        jam: new Date().toTimeString().slice(0, 5),
        toko: kunjungan.toko,
        alamat: kunjungan.alamat,
        kontak_person: kunjungan.kontak_person,
        jabatan: kunjungan.jabatan || '',
        telp: kunjungan.telp,
        email: kunjungan.email || '',
        tipe_kunjungan: visitPlan?.tipe_kunjungan || 'Akuisisi',
        hasil_kunjungan: kunjungan.hasil_kunjungan,
        catatan: kunjungan.catatan || '',
        tindak_lanjut: kunjungan.tindak_lanjut || '',
        potensi_order: kunjungan.potensi_order || '',
        status: 'selesai'
      }));
      
      if (navigator.onLine) {
        try {
          console.log('Saving realisasi to server...');
          const savedItems = [];
          let successCount = 0;
          let errorCount = 0;
          
          // Save menggunakan endpoint yang sudah tested berhasil
          const token = localStorage.getItem('jwtToken') || localStorage.getItem('token');
          
          for (const realisasiData of realisasiKunjunganData) {
            try {
              const response = await axios.post('/api/rr/realisasi/', realisasiData, {
                headers: {
                  'Authorization': `Bearer ${token}`,
                  'Content-Type': 'application/json'
                }
              });
              
              if (response.data && response.data.id) {
                savedItems.push(response.data);
                successCount++;
                console.log(`Realisasi ${realisasiData.toko} berhasil disimpan`);
              }
            } catch (itemError) {
              console.error(`Error saving realisasi ${realisasiData.toko}:`, itemError);
              errorCount++;
              
              // Simpan ke localStorage jika gagal
              const offlineRealisasi = JSON.parse(localStorage.getItem('offlineRRRealisasi') || '[]');
              offlineRealisasi.unshift({
                ...realisasiData,
                id: Date.now() + Math.random(),
                created_at: new Date().toISOString(),
                offline: true
              });
              localStorage.setItem('offlineRRRealisasi', JSON.stringify(offlineRealisasi));
            }
          }
          
          if (successCount > 0) {
            toast.success(`${successCount} realisasi kunjungan berhasil disimpan!`);
          }
          
          if (errorCount > 0) {
            toast.warning(`${errorCount} realisasi disimpan offline dan akan disinkronkan nanti.`);
          }
          
          // Update status plan kunjungan jika ada
          if (visitPlan?.id && successCount > 0) {
            try {
              await axios.post(`/api/rr/visits/${visitPlan.id}/complete/`, {}, {
                headers: { Authorization: `Bearer ${token}` }
              });
            } catch (updateError) {
              console.warn('Failed to update plan status:', updateError);
            }
          }
          
        } catch (apiError) {
          console.error('API Error:', apiError);
          
          if (apiError.response?.status === 401) {
            toast.error('Sesi login telah berakhir. Silakan login ulang.');
            navigate('/login');
            return;
          }
          
          // Simpan semua ke localStorage jika API gagal total
          const offlineRealisasi = JSON.parse(localStorage.getItem('offlineRRRealisasi') || '[]');
          realisasiKunjunganData.forEach(data => {
            offlineRealisasi.unshift({
              ...data,
              id: Date.now() + Math.random(),
              created_at: new Date().toISOString(),
              offline: true
            });
          });
          localStorage.setItem('offlineRRRealisasi', JSON.stringify(offlineRealisasi));
          
          toast.warning('Server tidak tersedia. Data disimpan offline dan akan disinkronkan nanti.');
        }
      } else {
        // Mode offline
        const offlineRealisasi = JSON.parse(localStorage.getItem('offlineRRRealisasi') || '[]');
        realisasiKunjunganData.forEach(data => {
          offlineRealisasi.unshift({
            ...data,
            id: Date.now() + Math.random(),
            created_at: new Date().toISOString(),
            offline: true
          });
        });
        localStorage.setItem('offlineRRRealisasi', JSON.stringify(offlineRealisasi));
        
        toast.info('Mode offline: Data disimpan dan akan disinkronkan saat online.');
      }
      
      // Redirect ke dashboard setelah berhasil
      setTimeout(() => {
        navigate('/dashboard/marketing/retail');
      }, 2000);
      
    } catch (error) {
      console.error('Error submitting realisasi:', error);
      toast.error('Terjadi kesalahan saat menyimpan data.');
    } finally {
      setSubmitting(false);
    }
  };
  
  // Perbaiki button "Gunakan data plan" untuk mapping field yang benar
  const useDataFromPlan = () => {
    if (visitPlan && kunjunganList.length > 0) {
      setKunjunganList(kunjunganList.map((k, idx) => 
        idx === 0 ? { 
          ...k, 
          toko: visitPlan.tujuan_kunjungan || visitPlan.toko || '', 
          alamat: visitPlan.alamat || '',
          kontak_person: visitPlan.kontak_person || '',
          telp: visitPlan.telp || ''
        } : k
      ));
      toast.info("Data dari rencana kunjungan telah digunakan untuk form pertama");
    }
  };

  // Update saveRealisasiToServer function
  const saveRealisasiToServer = async (realisasiData) => {
    try {
      console.log('Saving realisasi to server:', realisasiData);
      
      // Gunakan endpoint yang sudah tested berhasil
      const response = await api.post('/rr/realisasi/', realisasiData);
      
      if (response.data && response.data.id) {
        console.log('Realisasi saved successfully:', response.data);
        return response.data;
      } else {
        throw new Error('Invalid response from server');
      }
    } catch (error) {
      console.error('Error saving realisasi:', error);
      
      if (error.response?.status === 401) {
        toast.error('Sesi login telah berakhir. Silakan login ulang.');
        navigate('/login');
        throw error;
      }
      
      const errorMessage = error.response?.data?.message || 
                          error.response?.data?.details || 
                          error.message || 
                          'Terjadi kesalahan saat menyimpan realisasi';
      
      toast.error(`Gagal menyimpan realisasi: ${errorMessage}`);
      throw error;
    }
  };

  if (loading) {
    return (
      <div className="text-center py-5">
        <Spinner animation="border" variant="primary" />
        <p className="mt-3">Memuat data kunjungan...</p>
      </div>
    );
  }
  
  return (
    <div className="form-realisasi-container">
      <Card>
        <Card.Header className="bg-primary text-white">
          <div className="d-flex align-items-center">
            <FontAwesomeIcon icon={faCalendarCheck} size="lg" className="me-2" />
            <h5 className="mb-0">Form Realisasi Multi Kunjungan</h5>
          </div>
        </Card.Header>
        
        <Card.Body>
          {error && (
            <Alert variant="danger" className="mb-4">
              <FontAwesomeIcon icon={faInfoCircle} className="me-2" />
              {error}
            </Alert>
          )}
          
          {visitPlan && (
            <Alert variant="info" className="mb-4">
              <FontAwesomeIcon icon={faInfoCircle} className="me-2" />
              <div className="d-flex justify-content-between align-items-center">
                <div>
                  <strong>Plan Kunjungan:</strong> {visitPlan.tujuan_kunjungan || visitPlan.toko} 
                  <div className="small mt-1">
                    <strong>Tanggal:</strong> {new Date(visitPlan.tanggal).toLocaleDateString('id-ID')} | 
                    <strong> Tipe:</strong> {visitPlan.tipe_kunjungan} | 
                    <strong> Catatan:</strong> {visitPlan.catatan || '-'}
                  </div>
                </div>
                <div>
                  <Button 
                    variant="outline-primary" 
                    size="sm"
                    onClick={useDataFromPlan}
                  >
                    Gunakan data plan untuk form pertama
                  </Button>
                </div>
              </div>
            </Alert>
          )}
          
          <Form onSubmit={handleSubmit}>
            <div className="d-flex justify-content-between align-items-center mb-3">
              <h5>Daftar Realisasi Kunjungan</h5>
              <Button 
                variant="success" 
                onClick={addKunjungan}
                disabled={kunjunganList.length >= 10}
              >
                <FontAwesomeIcon icon={faPlus} className="me-1" /> Tambah Kunjungan
              </Button>
            </div>
            
            {kunjunganList.map((kunjungan, index) => (
              <Card key={kunjungan.id} className="mb-4 border">
                <Card.Header className="d-flex justify-content-between align-items-center">
                  <h6 className="mb-0">Kunjungan #{index + 1}</h6>
                  {kunjunganList.length > 1 && (
                    <Button 
                      variant="danger" 
                      size="sm" 
                      onClick={() => removeKunjungan(kunjungan.id)}
                    >
                      <FontAwesomeIcon icon={faTrash} /> Hapus
                    </Button>
                  )}
                </Card.Header>
                <Card.Body>
                  <Row className="mb-3">
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>
                          <FontAwesomeIcon icon={faBuilding} className="me-2" />
                          Nama Perusahaan / Toko <span className="text-danger">*</span>
                        </Form.Label>
                        <Form.Control
                          type="text"
                          value={kunjungan.toko}
                          onChange={(e) => handleKunjunganChange(kunjungan.id, 'toko', e.target.value)}
                          placeholder="Nama perusahaan atau toko yang dikunjungi"
                          required
                        />
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>
                          <FontAwesomeIcon icon={faMapMarkerAlt} className="me-2" />
                          Alamat <span className="text-danger">*</span>
                        </Form.Label>
                        <Form.Control
                          as="textarea"
                          rows={1}
                          value={kunjungan.alamat}
                          onChange={(e) => handleKunjunganChange(kunjungan.id, 'alamat', e.target.value)}
                          placeholder="Alamat lengkap"
                          required
                        />
                      </Form.Group>
                    </Col>
                  </Row>
                  
                  <Row className="mb-3">
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>
                          <FontAwesomeIcon icon={faUser} className="me-2" />
                          Kontak Person <span className="text-danger">*</span>
                        </Form.Label>
                        <Form.Control
                          type="text"
                          value={kunjungan.kontak_person}
                          onChange={(e) => handleKunjunganChange(kunjungan.id, 'kontak_person', e.target.value)}
                          placeholder="Nama kontak person"
                          required
                        />
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>Jabatan</Form.Label>
                        <Form.Control
                          type="text"
                          value={kunjungan.jabatan}
                          onChange={(e) => handleKunjunganChange(kunjungan.id, 'jabatan', e.target.value)}
                          placeholder="Jabatan kontak person"
                        />
                      </Form.Group>
                    </Col>
                  </Row>
                  
                  <Row className="mb-3">
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>
                          <FontAwesomeIcon icon={faPhone} className="me-2" />
                          No. Telepon <span className="text-danger">*</span>
                        </Form.Label>
                        <Form.Control
                          type="text"
                          value={kunjungan.telp}
                          onChange={(e) => handleKunjunganChange(kunjungan.id, 'telp', e.target.value)}
                          placeholder="Nomor telepon/HP"
                          required
                        />
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>Email</Form.Label>
                        <Form.Control
                          type="email"
                          value={kunjungan.email}
                          onChange={(e) => handleKunjunganChange(kunjungan.id, 'email', e.target.value)}
                          placeholder="Email (opsional)"
                        />
                      </Form.Group>
                    </Col>
                  </Row>
                  
                  <Form.Group className="mb-3">
                    <Form.Label>Hasil Kunjungan <span className="text-danger">*</span></Form.Label>
                    <Form.Select
                      value={kunjungan.hasil_kunjungan}
                      onChange={(e) => handleKunjunganChange(kunjungan.id, 'hasil_kunjungan', e.target.value)}
                      required
                    >
                      <option value="">-- Pilih Hasil --</option>
                      <option value="Order Placed">Order Placed</option>
                      <option value="Follow Up Next Week">Follow Up Next Week</option>
                      <option value="Need Time To Consider">Need Time To Consider</option>
                      <option value="Not Interested">Not Interested</option>
                      <option value="Person Not Available">Person Not Available</option>
                      <option value="Other">Lainnya</option>
                    </Form.Select>
                  </Form.Group>
                  
                  <Form.Group className="mb-3">
                    <Form.Label>Potensi Order</Form.Label>
                    <Form.Control
                      type="text"
                      value={kunjungan.potensi_order}
                      onChange={(e) => handleKunjunganChange(kunjungan.id, 'potensi_order', e.target.value)}
                      placeholder="Potensi nilai order (jika ada)"
                    />
                  </Form.Group>
                  
                  <Row className="mb-3">
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>Tindak Lanjut</Form.Label>
                        <Form.Control
                          as="textarea"
                          rows={3}
                          value={kunjungan.tindak_lanjut}
                          onChange={(e) => handleKunjunganChange(kunjungan.id, 'tindak_lanjut', e.target.value)}
                          placeholder="Rencana tindak lanjut"
                        />
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>Catatan</Form.Label>
                        <Form.Control
                          as="textarea"
                          rows={3}
                          value={kunjungan.catatan}
                          onChange={(e) => handleKunjunganChange(kunjungan.id, 'catatan', e.target.value)}
                          placeholder="Catatan tambahan"
                        />
                      </Form.Group>
                    </Col>
                  </Row>
                </Card.Body>
              </Card>
            ))}
            
            <Alert variant="warning">
              <FontAwesomeIcon icon={faInfoCircle} className="me-2" />
              Pastikan semua data realisasi kunjungan sudah diisi dengan benar. 
              Anda dapat menambahkan hingga 10 kunjungan dalam satu laporan.
            </Alert>
            
            <div className="d-flex justify-content-between mt-4">
              <Button 
                variant="secondary" 
                onClick={() => navigate('/dashboard/marketing/retail')}
                disabled={submitting}
              >
                <FontAwesomeIcon icon={faTimes} className="me-1" /> Batal
              </Button>
              <Button 
                variant="success" 
                type="submit"
                disabled={submitting}
              >
                {submitting ? (
                  <>
                    <Spinner as="span" animation="border" size="sm" className="me-2" />
                    Menyimpan...
                  </>
                ) : (
                  <>
                    <FontAwesomeIcon icon={faSave} className="me-1" /> Simpan Semua Realisasi
                  </>
                )}
              </Button>
            </div>
          </Form>
        </Card.Body>
      </Card>
    </div>
  );
};

export default FormRealisasiMultiKunjungan;